import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { useAdCategoryStore } from "@/pages/ad-Categories/store/adCategoryStore";
import useGlobalStore from "@/store/globalStore";
import {
  Image as ImageIcon,
  X,
  Cloud,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Type,
  Square,
  Film,
  MousePointer,
  Pencil,
  Layers,
  GripVertical,
  Music,
  FileText,
} from "lucide-react";
import Button from "@/components/ui/Button";
import TextField from "@/components/ui/Textfield";
import TextareaField from "@/components/ui/Textareafield";
import Dropdown from "@/components/ui/DropDown";
import useAdBuilderStore from "./store/ad-builder-store";
import type { SubComponent, SaveComponentPayload, SaveLocationPayload } from "./store/ad-builder-store";

type ElementCategory =
  | "Text"
  | "Image"
  | "Video"
  | "Audio"
  | "Button"
  | "Shape"
  | "Preview"
  | "Attributes";

interface Template {
  id: string;
  label: string;
  category: ElementCategory;
}

interface MediaUpload {
  id: string;
  elementId: string;
  file: File | null;
  preview: string | null;
  slotIndex?: number; // For carousel items
}

interface FloatingMenu {
  type:
    | "text-settings"
    | "properties"
    | "shape-properties"
    | "image-properties"
    | "video-properties";
  elementId: string;
  x: number;
  y: number;
}

// ─── Interface canvas dimensions ──────────────────────────────────────────────
const DEFAULT_CANVAS = { width: 375, height: 667, rounded: "rounded-[2rem]" };

const AdBuilder: React.FC = () => {
  const [searchParams] = useSearchParams();
  const adCategoryMappingId = searchParams.get("adCategoryMappingId");

  // Ad-builder store
  const {
    canvasComponents,
    isLoadingComponents,
    fetchCanvasComponents,
    loadCanvas,
    saveMaster,
    saveComponents,
    saveLocations,
    saveAdInfo,
    saveMedia,
    markDirty,
    canvasMasterId,
    isSaving,
    loadedCanvasData,
    isLoadingCanvas,
  } = useAdBuilderStore();

  // Read selected interface/specs/blocks from adCategoryStore
  const {
    selectedInterface,
    selectedAdSpecs,
    selectedAdInfoBlocks,
    canvasBuilderData,
    setSelectedInterface,
    setSelectedAdSpecs,
    setSelectedAdInfoBlocks,
  } = useAdCategoryStore(
    useShallow((state) => ({
      selectedInterface: state.selectedInterface,
      selectedAdSpecs: state.selectedAdSpecs,
      selectedAdInfoBlocks: state.selectedAdInfoBlocks,
      canvasBuilderData: state.canvasBuilderData,
      setSelectedInterface: state.setSelectedInterface,
      setSelectedAdSpecs: state.setSelectedAdSpecs,
      setSelectedAdInfoBlocks: state.setSelectedAdInfoBlocks,
    })),
  );

  // Global toast
  const { showSuccess, setError } = useGlobalStore(
    useShallow((state) => ({
      showSuccess: state.showSuccess,
      setError: state.setError,
    })),
  );

  // Derive canvas dimensions directly from the API canvas_info of the selected interface
  const canvasDimensions = useMemo(() => {
    if (!selectedInterface || !canvasBuilderData?.interface_types) return DEFAULT_CANVAS;
    const found = canvasBuilderData.interface_types.find(
      (t: any) => t.id === selectedInterface,
    );
    if (!found?.canvas_info?.width || !found?.canvas_info?.height) return DEFAULT_CANVAS;
    const rounded = found.name === "mobile" ? "rounded-[2rem]" : "rounded-lg";
    return { width: found.canvas_info.width, height: found.canvas_info.height, rounded };
  }, [selectedInterface, canvasBuilderData]);

  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [canvasElements, setCanvasElements] = useState<any[]>([]);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [floatingMenu, setFloatingMenu] = useState<FloatingMenu | null>(null);
  const [draggedTemplate, setDraggedTemplate] = useState<Template | null>(null);
  const [showLayersPanel, setShowLayersPanel] = useState(true);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Content form state - dynamic values keyed by sub-component key
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [mediaUploads, setMediaUploads] = useState<MediaUpload[]>([]);

  // Layer reorder drag state
  const [draggedLayerIndex, setDraggedLayerIndex] = useState<number | null>(
    null,
  );
  const [dragOverLayerIndex, setDragOverLayerIndex] = useState<number | null>(
    null,
  );

  // Fetch API data and load existing canvas on mount
  useEffect(() => {
    fetchCanvasComponents();
    if (adCategoryMappingId) {
      loadCanvas(adCategoryMappingId).catch(() => {
        // No canvas yet — that's fine, user will create one on save
      });
    }
  }, [fetchCanvasComponents, loadCanvas, adCategoryMappingId]);

  // Hydrate canvas elements and step 5 selections from loaded canvas data
  useEffect(() => {
    if (isLoadingCanvas || !loadedCanvasData) return;

    const canvasInfo = loadedCanvasData.canvas_master?.canvas_info_aggregated ?? {};

    // Restore step 5 selections saved in canvas_info_aggregated
    if (canvasInfo.selectedInterface) {
      setSelectedInterface(canvasInfo.selectedInterface);
    }
    if (Array.isArray(canvasInfo.selectedAdSpecs)) {
      setSelectedAdSpecs(canvasInfo.selectedAdSpecs);
    }
    if (Array.isArray(canvasInfo.selectedAdInfoBlocks)) {
      setSelectedAdInfoBlocks(canvasInfo.selectedAdInfoBlocks);
    }

    // Hydrate canvas elements from saved components
    if (!loadedCanvasData.components?.length) return;

    // Use saved interface name to pick the right location; fall back to "desktop"
    const savedInterfaceName = canvasInfo.selectedInterface
      ? canvasBuilderData?.interface_types?.find((t: any) => t.id === canvasInfo.selectedInterface)?.name ?? "desktop"
      : "desktop";

    const hydrated = loadedCanvasData.components.map((component: any) => {
      const loc = component.locations?.[savedInterfaceName]?.location_json;
      const agg = component.aggregated_json ?? {};

      // Collect location DB ids keyed by interface name for use on update
      const locationIds: Record<string, string> = {};
      if (component.locations) {
        Object.entries(component.locations).forEach(([iface, locData]: [string, any]) => {
          if (locData?.id) locationIds[iface] = locData.id;
        });
      }

      return {
        id: `element-${component.id}`,
        dbId: component.id,
        locationIds,
        layerName: component.canvas_component_sub_master_display_name,
        label: agg.label ?? component.canvas_component_sub_master_display_name,
        visible: true,
        templateId: component.canvas_component_sub_master_name,
        category: component.canvas_component_master_display_name,
        x: loc?.x ?? 100,
        y: loc?.y ?? 100,
        width: loc?.width ?? 200,
        height: loc?.height ?? 100,
        content: agg.content,
        textSettings: agg.textSettings,
        shapeSettings: agg.shapeSettings,
        buttonSettings: agg.buttonSettings,
        properties: {
          showInTemplateOnly: false,
          showInInfoOnly: false,
          showCharacterCount: false,
        },
      };
    });

    setCanvasElements(hydrated);
  }, [loadedCanvasData, isLoadingCanvas, canvasBuilderData]);

  // ── Save all canvas data ──────────────────────────────────────────────────
  const saveAll = useCallback(
    async (status: "draft" | "completed") => {
      if (!adCategoryMappingId) {
        setError("No ad category mapping ID found. Please navigate from Step 5.");
        return;
      }
      try {
        // 1. Save / update canvas master
        const masterId = await saveMaster(adCategoryMappingId, status, {
          canvasWidth: canvasDimensions.width,
          canvasHeight: canvasDimensions.height,
          status,
          selectedInterface,
          selectedAdSpecs,
          selectedAdInfoBlocks,
        });

        // 2. Build component payloads — map canvas elements to API shape
        const componentPayloads: SaveComponentPayload[] = canvasElements
          .map((el) => {
            // Find the master and sub-master from the hierarchy
            const masterComponent = canvasComponents.find(
              (c) => c.display_name === el.category,
            );
            const subComponent = masterComponent?.sub_components.find(
              (s) => s.name === el.templateId,
            );
            if (!masterComponent || !subComponent) return null;

            const aggregated_json: Record<string, any> = {
              label: el.label,
            };
            if (el.content !== undefined) aggregated_json.content = el.content;
            if (el.textSettings) aggregated_json.textSettings = el.textSettings;
            if (el.shapeSettings) aggregated_json.shapeSettings = el.shapeSettings;
            if (el.buttonSettings) aggregated_json.buttonSettings = el.buttonSettings;

            const payload: SaveComponentPayload = {
              canvas_component_master_id: masterComponent.id,
              canvas_component_sub_master_id: subComponent.id,
              aggregated_json,
            };
            // Include existing DB id if available (for updates)
            if (el.dbId) payload.id = el.dbId;
            return payload;
          })
          .filter(Boolean) as SaveComponentPayload[];

        if (componentPayloads.length === 0) {
          showSuccess(
            status === "draft"
              ? "Progress saved (no components)."
              : "Template saved (no components).",
          );
          return;
        }

        // 3. Save components — get back DB ids
        const savedComponents = await saveComponents(masterId, componentPayloads);

        // Build a map: canvas element id → saved component DB id
        // For elements that already had a dbId (updates), use the response record
        // that matches by id; for new elements, match positionally within new-only items.
        const elementToDbId: Record<string, string> = {};
        // Pre-seed with existing dbIds so updates are resolved first
        canvasElements.forEach((el) => {
          if (el.dbId) elementToDbId[el.id] = el.dbId;
        });
        // For newly created elements (no dbId), match against savedComponents that are new
        const newElements = canvasElements.filter((el) => !el.dbId && componentPayloads.find((p) => !p.id));
        const newSaved = savedComponents.filter((sc: any) => !componentPayloads.find((p) => p.id === sc.id));
        newElements.forEach((el, i) => {
          if (newSaved[i]) elementToDbId[el.id] = newSaved[i].id;
        });

        // Persist DB ids back onto canvas elements for future updates
        setCanvasElements((prev) =>
          prev.map((el) =>
            elementToDbId[el.id] ? { ...el, dbId: elementToDbId[el.id] } : el,
          ),
        );

        // Derive interface name for location id lookup
        const currentInterfaceName =
          canvasBuilderData?.interface_types?.find((t: any) => t.id === selectedInterface)?.name ?? "";

        // 4. Save locations (using selectedInterface) & ad info in parallel
        const locationPayloads: SaveLocationPayload[] = canvasElements
          .map((el, idx) => {
            const masterComponent = canvasComponents.find(
              (c) => c.display_name === el.category,
            );
            const subComponent = masterComponent?.sub_components.find(
              (s) => s.name === el.templateId,
            );
            const dbId = elementToDbId[el.id] || el.dbId;
            if (!masterComponent || !subComponent || !dbId) return null;

            const payload: SaveLocationPayload = {
              canvas_component_master_id: masterComponent.id,
              canvas_component_sub_master_id: subComponent.id,
              canvas_component_info_id: dbId,
              interface_id: selectedInterface,
              location_json: {
                x: Math.round(el.x),
                y: Math.round(el.y),
                width: Math.round(el.width),
                height: Math.round(el.height),
                z_index: idx + 1,
              },
            };

            // Include existing location DB id so the backend updates instead of creating
            const existingLocationId = el.locationIds?.[currentInterfaceName];
            if (existingLocationId) payload.id = existingLocationId;

            return payload;
          })
          .filter(Boolean) as SaveLocationPayload[];

        const savedLocations = await saveLocations(masterId, locationPayloads);

        // Persist returned location ids back onto canvas elements for future updates
        if (Array.isArray(savedLocations) && currentInterfaceName) {
          setCanvasElements((prev) =>
            prev.map((el) => {
              const dbId = elementToDbId[el.id] || el.dbId;
              const savedLoc = savedLocations.find((l: any) => l.canvas_component_info_id === dbId);
              if (!savedLoc) return el;
              return {
                ...el,
                locationIds: { ...(el.locationIds ?? {}), [currentInterfaceName]: savedLoc.id },
              };
            }),
          );
        }

        // 5. Upload media files sequentially
        for (const upload of mediaUploads) {
          if (!upload.file) continue;
          const dbId = elementToDbId[upload.elementId] ||
            canvasElements.find((el) => el.id === upload.elementId)?.dbId;
          if (!dbId) continue;
          await saveMedia(masterId, dbId, upload.file, upload.slotIndex ?? 0);
        }

        markDirty("master"); // reset: all clean
        showSuccess(
          status === "draft" ? "Progress saved successfully." : "Template saved successfully.",
        );
      } catch (err: any) {
        setError(err?.message || "Save failed. Please try again.");
      }
    },
    [
      adCategoryMappingId,
      canvasElements,
      canvasComponents,
      mediaUploads,
      selectedInterface,
      selectedAdSpecs,
      selectedAdInfoBlocks,
      canvasDimensions,
      canvasBuilderData,
      saveMaster,
      saveComponents,
      saveLocations,
      saveMedia,
      markDirty,
      showSuccess,
      setError,
    ],
  );

  // Set default category when data loads
  useEffect(() => {
    if (canvasComponents.length > 0 && !selectedCategory) {
      setSelectedCategory(canvasComponents[0].display_name);
    }
  }, [canvasComponents, selectedCategory]);

  // Derive categories from API data
  const categories = canvasComponents.map((c) => c.display_name);

  // Get templates from API data for selected category
  const getCategoryTemplates = (categoryName: string): Template[] => {
    const component = canvasComponents.find(
      (c) => c.display_name === categoryName,
    );
    if (!component) return [];
    return component.sub_components
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((sub) => ({
        id: sub.name,
        label: sub.display_name,
        category: component.display_name as ElementCategory,
      }));
  };

  // Get sub-component data by name from API
  const getSubComponent = (
    categoryName: string,
    subName: string,
  ): SubComponent | undefined => {
    const component = canvasComponents.find(
      (c) => c.display_name === categoryName,
    );
    return component?.sub_components.find((s) => s.name === subName);
  };

  // Helper: collect all sub-components of specific categories from canvas elements
  const getSubComponentsByCategory = (
    componentCategory: string,
  ): SubComponent[] => {
    const subs: SubComponent[] = [];
    canvasElements.forEach((el) => {
      const sub = getSubComponent(el.category, el.templateId);
      if (sub && sub.component_category === componentCategory) {
        subs.push(sub);
      }
    });
    return subs;
  };

  // Update dynamic form value
  const updateFormValue = (key: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  // Get button settings from API data
  const getButtonSettings = (templateId: string) => {
    const sub = getSubComponent("Button", templateId);
    if (sub?.settings) {
      return {
        bg: sub.settings.bg || "#333333",
        text: sub.settings.text_color || "#FFFFFF",
        label: sub.settings.label || "Click Here",
        icon: "",
        borderColor: sub.settings.border_color,
        borderRadius: sub.settings.border_radius || "8px",
        shadow: sub.settings.shadow,
      };
    }
    return {
      bg: "#333333",
      text: "#FFFFFF",
      label: "Click Here",
      icon: "",
      borderRadius: "8px",
    };
  };

  const addElementToCanvas = (
    template: Template,
    dropX?: number,
    dropY?: number,
  ) => {
    const newElement = {
      id: `element-${Date.now()}`,
      layerName: template.label,
      visible: true,
      templateId: template.id,
      label: template.label,
      category: template.category,
      x: dropX ?? 100,
      y: dropY ?? 100,
      width: template.category === "Button" ? 200 : 200,
      height: template.category === "Button" ? 48 : 100,
      content: template.category === "Text" ? "Content Title Goes Here" : "",
      properties: {
        showInTemplateOnly: false,
        showInInfoOnly: false,
        showCharacterCount: false,
      },
      textSettings:
        template.category === "Text"
          ? {
              font: "",
              fontSize: 12,
              bold: false,
              italic: false,
              underline: false,
              strikethrough: false,
              color: "#000000",
            }
          : undefined,
      shapeSettings:
        template.category === "Shape"
          ? {
              background: true,
              overlay: true,
              color: "#EA33C0",
            }
          : undefined,
      buttonSettings:
        template.category === "Button"
          ? getButtonSettings(template.id)
          : undefined,
    };

    setCanvasElements([...canvasElements, newElement]);
    setSelectedElement(newElement.id);

    // Create media upload slots for Image/Video/Audio/Preview elements
    if (
      template.category === "Image" ||
      template.category === "Video" ||
      template.category === "Audio" ||
      template.category === "Preview"
    ) {
      const isCarousel = template.id.includes("carousel");
      const isPlaylist = template.id.includes("playlist");
      const slotsCount = isCarousel || isPlaylist ? 5 : 1; // Carousels/Playlists get 5 slots, single items get 1

      const newUploads: MediaUpload[] = [];
      for (let i = 0; i < slotsCount; i++) {
        newUploads.push({
          id: `upload-${newElement.id}-${i}`,
          elementId: newElement.id,
          file: null,
          preview: null,
          slotIndex: isCarousel || isPlaylist ? i : undefined,
        });
      }

      setMediaUploads([...mediaUploads, ...newUploads]);
    }
  };

  const handleDragStart = (template: Template) => {
    setDraggedTemplate(template);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedTemplate && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      addElementToCanvas(draggedTemplate, x - 100, y - 50);
      setDraggedTemplate(null);
    }
  };

  const openFloatingMenu = (
    type: FloatingMenu["type"],
    elementId: string,
    x: number,
    y: number,
  ) => {
    setFloatingMenu({ type, elementId, x, y });
  };

  const closeFloatingMenu = () => {
    setFloatingMenu(null);
  };

  const updateElementProperty = (
    elementId: string,
    path: string,
    value: any,
  ) => {
    setCanvasElements((prev) =>
      prev.map((el) => {
        if (el.id === elementId) {
          const keys = path.split(".");
          if (keys.length === 1) {
            return { ...el, [keys[0]]: value };
          } else if (keys.length === 2) {
            return {
              ...el,
              [keys[0]]: {
                ...el[keys[0]],
                [keys[1]]: value,
              },
            };
          }
        }
        return el;
      }),
    );
  };

  const updateElementMultipleProperties = (
    elementId: string,
    updates: Record<string, any>,
  ) => {
    setCanvasElements((prev) =>
      prev.map((el) => {
        if (el.id === elementId) {
          return { ...el, ...updates };
        }
        return el;
      }),
    );
  };

  const selectedElementData = canvasElements.find(
    (el) => el.id === selectedElement,
  );

  const handleMediaUpload = (
    uploadId: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setMediaUploads(
        mediaUploads.map((upload) =>
          upload.id === uploadId
            ? { ...upload, file, preview: URL.createObjectURL(file) }
            : upload,
        ),
      );
    }
  };

  const removeMediaUpload = (uploadId: string) => {
    setMediaUploads(
      mediaUploads.map((upload) =>
        upload.id === uploadId
          ? { ...upload, file: null, preview: null }
          : upload,
      ),
    );
  };

  const removeCanvasElement = (elementId: string) => {
    // Remove element from canvas
    setCanvasElements(canvasElements.filter((el) => el.id !== elementId));
    // Remove associated media uploads
    setMediaUploads(
      mediaUploads.filter((upload) => upload.elementId !== elementId),
    );
    // Clear selection if this element was selected
    if (selectedElement === elementId) {
      setSelectedElement(null);
    }
    // Close floating menu if open
    closeFloatingMenu();
  };

  const toggleLayerVisibility = (elementId: string) => {
    setCanvasElements((prev) =>
      prev.map((el) =>
        el.id === elementId ? { ...el, visible: !el.visible } : el,
      ),
    );
  };

  const renameLayer = (elementId: string, newName: string) => {
    setCanvasElements((prev) =>
      prev.map((el) =>
        el.id === elementId ? { ...el, layerName: newName } : el,
      ),
    );
  };

  // Layer reorder: move element from one position to another
  const reorderLayers = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setCanvasElements((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  };

  // Get media uploads grouped by element
  const getMediaUploadsByCategory = () => {
    const imageUploads = mediaUploads.filter((upload) => {
      const element = canvasElements.find((el) => el.id === upload.elementId);
      return element && element.category === "Image";
    });

    const videoUploads = mediaUploads.filter((upload) => {
      const element = canvasElements.find((el) => el.id === upload.elementId);
      return element && element.category === "Video";
    });

    const audioUploads = mediaUploads.filter((upload) => {
      const element = canvasElements.find((el) => el.id === upload.elementId);
      return element && element.category === "Audio";
    });

    const previewUploads = mediaUploads.filter((upload) => {
      const element = canvasElements.find((el) => el.id === upload.elementId);
      return element && element.category === "Preview";
    });

    return { imageUploads, videoUploads, audioUploads, previewUploads };
  };

  return (
    <div className="flex flex-col h-screen bg-[#EDEDED]">
      {/* Top Header */}
      <div className="bg-[#e7e7e70e] px-8 pt-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-black mb-2">
              Create Ad Type Template
            </h1>
          </div>
          <div className="flex gap-3">
            <Button type="secondary" enableHover={false}>
              Previous
            </Button>
            <Button
              type="secondary"
              leadingIcon={<Cloud size={18} />}
              onClick={() => saveAll("draft")}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Progress"}
            </Button>
            <Button
              type="primary"
              onClick={() => saveAll("completed")}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Template"}
            </Button>
          </div>
        </div>
      </div>

      {/* Subtitle */}
      <div className="bg-[#e7e7e70e] px-8 py-4">
        <p className="text-black">
          Define Template Attributes for {canvasBuilderData?.ad_type?.name ?? "[Ad Type]"}
        </p>
      </div>

      {/* Pink Top Bar */}
      <hr className="border-[#EA33C0]" />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        <div className="w-40 bg-[#dedede72] p-4">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`w-full text-left px-3 py-3 mb-1 transition-colors text-base relative ${
                selectedCategory === category
                  ? "text-[#EA33C0] font-semibold bg-transparent pl-2.5"
                  : "text-black hover:bg-gray-200 font-normal"
              }`}
            >
              {selectedCategory === category && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 h-[50%] w-[3px] bg-[#EA33C0] rounded" />
              )}
              {category}
            </button>
          ))}
        </div>

        {/* Template Options */}
        <div className="w-56 bg-[#F5F5F5] p-5 overflow-y-auto">
          <div className="space-y-3">
            {getCategoryTemplates(selectedCategory).map((template) => (
              <div
                key={template.id}
                draggable
                onDragStart={() => handleDragStart(template)}
                className="cursor-move"
              >
                <Button
                  type="outline"
                  onClick={() => addElementToCanvas(template)}
                  className="text-black border-gray-700/15 w-full"
                  enableHover={false}
                >
                  {template.label}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Canvas Area */}
        <div
          className="flex-1 flex flex-col bg-[#F5F5F5] overflow-y-auto"
          onClick={() => {
            setSelectedElement(null);
            closeFloatingMenu();
          }}
        >
          {/* Canvas */}
          <div className="flex items-start justify-center p-8">
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              {/* Canvas Container - dimensions driven by selected interface */}
              <div
                ref={canvasRef}
                className={`relative bg-white ${canvasDimensions.rounded} border-[3px] border-[#EA33C0]`}
                style={{ width: `${canvasDimensions.width}px`, height: `${canvasDimensions.height}px` }}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setSelectedElement(null);
                    closeFloatingMenu();
                  }
                }}
              >
                {canvasElements.map(
                  (element) =>
                    element.visible !== false && (
                      <CanvasElement
                        key={element.id}
                        element={element}
                        isSelected={element.id === selectedElement}
                        onSelect={() => setSelectedElement(element.id)}
                        canvasWidth={canvasDimensions.width}
                        canvasHeight={canvasDimensions.height}
                        onOpenTextSettings={(x, y) =>
                          openFloatingMenu("text-settings", element.id, x, y)
                        }
                        onOpenProperties={(x, y) =>
                          openFloatingMenu("properties", element.id, x, y)
                        }
                        onOpenShapeProperties={(x, y) =>
                          openFloatingMenu("shape-properties", element.id, x, y)
                        }
                        onUpdatePosition={(x, y) => {
                          updateElementMultipleProperties(element.id, {
                            x,
                            y,
                          });
                        }}
                        onUpdateSize={(width, height) => {
                          updateElementMultipleProperties(element.id, {
                            width,
                            height,
                          });
                        }}
                        onDelete={() => removeCanvasElement(element.id)}
                      />
                    ),
                )}

                {canvasElements.length === 0 && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <ImageIcon size={80} className="text-gray-400 mb-3" />
                    <p className="text-gray-400 text-sm">
                      Drag and drop elements here
                    </p>
                  </div>
                )}
              </div>

              {/* Floating Menus */}
              {floatingMenu &&
                floatingMenu.type === "text-settings" &&
                selectedElementData && (
                  <FloatingTextSettings
                    element={selectedElementData}
                    position={{ x: floatingMenu.x, y: floatingMenu.y }}
                    onClose={closeFloatingMenu}
                    onUpdate={(key, value) =>
                      updateElementProperty(
                        selectedElementData.id,
                        `textSettings.${key}`,
                        value,
                      )
                    }
                    onUpdateContent={(value) =>
                      updateElementProperty(
                        selectedElementData.id,
                        "content",
                        value,
                      )
                    }
                  />
                )}

              {floatingMenu &&
                floatingMenu.type === "properties" &&
                selectedElementData && (
                  <FloatingProperties
                    element={selectedElementData}
                    position={{ x: floatingMenu.x, y: floatingMenu.y }}
                    onClose={closeFloatingMenu}
                    onUpdate={(key, value) =>
                      updateElementProperty(
                        selectedElementData.id,
                        `properties.${key}`,
                        value,
                      )
                    }
                    onUpdateLabel={(value) =>
                      updateElementProperty(
                        selectedElementData.id,
                        "label",
                        value,
                      )
                    }
                  />
                )}

              {floatingMenu &&
                floatingMenu.type === "shape-properties" &&
                selectedElementData && (
                  <FloatingShapeProperties
                    element={selectedElementData}
                    position={{ x: floatingMenu.x, y: floatingMenu.y }}
                    onClose={closeFloatingMenu}
                    onUpdate={(key, value) =>
                      updateElementProperty(
                        selectedElementData.id,
                        `shapeSettings.${key}`,
                        value,
                      )
                    }
                  />
                )}
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Bottom Content Form */}
          <div className="p-10 w-full">
            {/* Objectives Section */}
            {(() => {
              const objectiveSubs = getSubComponentsByCategory("objective");
              if (objectiveSubs.length === 0) return null;
              return (
                <div className="mb-10">
                  <h2 className="text-2xl font-semibold text-black mb-2">
                    Objectives
                  </h2>
                  <hr className="border-gray-200 mb-4" />
                  <div className="grid grid-cols-4 gap-6">
                    {objectiveSubs.map((sub) => (
                      <div key={sub.id}>
                        <label className="block text-sm font-semibold mb-3 text-black">
                          {sub.settings.label || sub.display_name}
                        </label>
                        {sub.settings.input_type === "select" &&
                        sub.settings.options ? (
                          <Dropdown
                            selected={
                              formValues[sub.key] ||
                              sub.settings.options[0]?.value ||
                              ""
                            }
                            setSelected={(val: string) =>
                              updateFormValue(sub.key, val)
                            }
                            options={sub.settings.options}
                            className="w-full"
                          />
                        ) : (
                          <TextField
                            type="text"
                            value={formValues[sub.key] || ""}
                            onChange={(e) =>
                              updateFormValue(sub.key, e.target.value)
                            }
                            placeholder={
                              sub.settings.placeholder || "Enter value..."
                            }
                            className="w-full"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Content Section */}
            {(() => {
              const contentSubs = getSubComponentsByCategory("content");
              if (contentSubs.length === 0) return null;
              return (
                <div className="mb-10">
                  <h2 className="text-2xl font-semibold text-black mb-2">
                    Content
                  </h2>
                  <hr className="border-gray-200 mb-4" />
                  <div className="grid grid-cols-2 gap-8">
                    {contentSubs.map((sub) => (
                      <div key={sub.id}>
                        <label className="block text-sm font-semibold mb-3 text-black">
                          {sub.settings.label || sub.display_name}
                        </label>
                        {sub.settings.input_type === "textarea" ? (
                          <>
                            <TextareaField
                              value={formValues[sub.key] || ""}
                              onChange={(e) =>
                                updateFormValue(sub.key, e.target.value)
                              }
                              placeholder={
                                sub.settings.placeholder ||
                                "Placeholder text...."
                              }
                              rows={sub.settings.rows || 4}
                              maxLength={sub.settings.character_limit}
                              className="w-full"
                            />
                            {sub.settings.character_limit && (
                              <p className="text-right text-sm text-gray-500 mt-2">
                                {(formValues[sub.key] || "").length}/
                                {sub.settings.character_limit}
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            <TextField
                              type="text"
                              value={formValues[sub.key] || ""}
                              onChange={(e) =>
                                updateFormValue(sub.key, e.target.value)
                              }
                              placeholder={
                                sub.settings.placeholder ||
                                "Placeholder text...."
                              }
                              maxLength={sub.settings.character_limit}
                              className="w-full"
                            />
                            {sub.settings.character_limit && (
                              <p className="text-right text-sm text-gray-500 mt-2">
                                {(formValues[sub.key] || "").length}/
                                {sub.settings.character_limit}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Media Section */}
            <div>
              <h3 className="text-xl font-semibold text-black mb-2">Media</h3>

              <hr className="border-gray-200 mb-4" />

              {(() => {
                const { imageUploads, videoUploads, audioUploads, previewUploads } =
                  getMediaUploadsByCategory();
                const hasMedia =
                  imageUploads.length > 0 || videoUploads.length > 0 || audioUploads.length > 0 || previewUploads.length > 0;

                if (!hasMedia) {
                  return (
                    <p className="text-sm text-gray-400 italic">
                      No media elements added yet. Add Image, Video, Audio, or Preview elements
                      from the sidebar.
                    </p>
                  );
                }

                return (
                  <>
                    {/* Image Uploads */}
                    {imageUploads.length > 0 && (
                      <div className="mb-6">
                        <p className="text-sm text-gray-500 mb-4">
                          {imageUploads.length}{" "}
                          {imageUploads.length === 1 ? "Image" : "Images"}
                        </p>
                        <div className="flex flex-wrap gap-4">
                          {imageUploads.map((upload, index) => {
                            const element = canvasElements.find(
                              (el) => el.id === upload.elementId,
                            );
                            return (
                              <div key={upload.id} className="relative">
                                {/* Number Badge */}
                                <div className="absolute -top-2.5 -left-2.5 w-6 h-6 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center text-xs font-semibold z-10 shadow-sm">
                                  {index + 1}
                                </div>

                                {/* Remove Element Button */}
                                <button
                                  onClick={() =>
                                    removeCanvasElement(upload.elementId)
                                  }
                                  className="absolute -top-2.5 -right-2.5 w-6 h-6 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center hover:bg-red-50 z-10 shadow-sm"
                                  title={`Remove ${element?.label || "element"}`}
                                >
                                  <X
                                    size={14}
                                    className="text-gray-600 hover:text-red-500"
                                  />
                                </button>

                                {upload.preview ? (
                                  <div className="relative w-32 h-32 border-2 border-gray-300 rounded-lg overflow-hidden group">
                                    <img
                                      src={upload.preview}
                                      alt="Uploaded"
                                      className="w-full h-full object-cover"
                                    />
                                    <button
                                      onClick={() =>
                                        removeMediaUpload(upload.id)
                                      }
                                      className="absolute top-1.5 right-1.5 w-5 h-5 bg-white rounded-full flex items-center justify-center hover:bg-red-50 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X size={12} className="text-red-500" />
                                    </button>
                                  </div>
                                ) : (
                                  <label className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#EA33C0] hover:bg-purple-50 transition-colors bg-white">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) =>
                                        handleMediaUpload(upload.id, e)
                                      }
                                      className="hidden"
                                    />
                                    <div className="text-4xl text-gray-300 font-light mb-1">
                                      +
                                    </div>
                                    <p className="text-xs text-gray-400 font-medium text-center px-2">
                                      Image (30MB max)
                                    </p>
                                  </label>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Video Uploads */}
                    {videoUploads.length > 0 && (
                      <div className="mb-6">
                        <p className="text-sm text-gray-500 mb-4">
                          {videoUploads.length}{" "}
                          {videoUploads.length === 1 ? "Video" : "Videos"}
                        </p>
                        <div className="flex flex-wrap gap-4">
                          {videoUploads.map((upload, index) => {
                            const element = canvasElements.find(
                              (el) => el.id === upload.elementId,
                            );
                            return (
                              <div key={upload.id} className="relative">
                                {/* Number Badge */}
                                <div className="absolute -top-2.5 -left-2.5 w-6 h-6 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center text-xs font-semibold z-10 shadow-sm">
                                  {index + 1}
                                </div>

                                {/* Remove Element Button */}
                                <button
                                  onClick={() =>
                                    removeCanvasElement(upload.elementId)
                                  }
                                  className="absolute -top-2.5 -right-2.5 w-6 h-6 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center hover:bg-red-50 z-10 shadow-sm"
                                  title={`Remove ${element?.label || "element"}`}
                                >
                                  <X
                                    size={14}
                                    className="text-gray-600 hover:text-red-500"
                                  />
                                </button>

                                {upload.preview ? (
                                  <div className="relative w-32 h-32 border-2 border-gray-300 rounded-lg overflow-hidden group">
                                    <video
                                      src={upload.preview}
                                      className="w-full h-full object-cover"
                                    />
                                    <button
                                      onClick={() =>
                                        removeMediaUpload(upload.id)
                                      }
                                      className="absolute top-1.5 right-1.5 w-5 h-5 bg-white rounded-full flex items-center justify-center hover:bg-red-50 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X size={12} className="text-red-500" />
                                    </button>
                                  </div>
                                ) : (
                                  <label className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#EA33C0] hover:bg-purple-50 transition-colors bg-white">
                                    <input
                                      type="file"
                                      accept="video/*"
                                      onChange={(e) =>
                                        handleMediaUpload(upload.id, e)
                                      }
                                      className="hidden"
                                    />
                                    <div className="text-4xl text-gray-300 font-light mb-1">
                                      +
                                    </div>
                                    <p className="text-xs text-gray-400 font-medium text-center px-2">
                                      Video (30MB max)
                                    </p>
                                  </label>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Audio Uploads */}
                    {audioUploads.length > 0 && (
                      <div className="mb-6">
                        <p className="text-sm text-gray-500 mb-4">
                          {audioUploads.length}{" "}
                          {audioUploads.length === 1 ? "Audio" : "Audio Files"}
                        </p>
                        <div className="flex flex-wrap gap-4">
                          {audioUploads.map((upload, index) => {
                            const element = canvasElements.find(
                              (el) => el.id === upload.elementId,
                            );
                            return (
                              <div key={upload.id} className="relative">
                                {/* Number Badge */}
                                <div className="absolute -top-2.5 -left-2.5 w-6 h-6 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center text-xs font-semibold z-10 shadow-sm">
                                  {index + 1}
                                </div>

                                {/* Remove Element Button */}
                                <button
                                  onClick={() =>
                                    removeCanvasElement(upload.elementId)
                                  }
                                  className="absolute -top-2.5 -right-2.5 w-6 h-6 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center hover:bg-red-50 z-10 shadow-sm"
                                  title={`Remove ${element?.label || "element"}`}
                                >
                                  <X
                                    size={14}
                                    className="text-gray-600 hover:text-red-500"
                                  />
                                </button>

                                {upload.preview ? (
                                  <div className="relative w-32 h-32 border-2 border-gray-300 rounded-lg overflow-hidden group bg-gradient-to-br from-purple-100 to-pink-100">
                                    <div className="w-full h-full flex flex-col items-center justify-center p-2">
                                      <Music className="text-[#EA33C0] mb-2" size={32} />
                                      <p className="text-xs text-gray-700 font-medium text-center truncate w-full">
                                        {upload.file?.name}
                                      </p>
                                    </div>
                                    <button
                                      onClick={() =>
                                        removeMediaUpload(upload.id)
                                      }
                                      className="absolute top-1.5 right-1.5 w-5 h-5 bg-white rounded-full flex items-center justify-center hover:bg-red-50 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X size={12} className="text-red-500" />
                                    </button>
                                  </div>
                                ) : (
                                  <label className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#EA33C0] hover:bg-purple-50 transition-colors bg-white">
                                    <input
                                      type="file"
                                      accept="audio/*,.mp3,.wav,.ogg,.aac,.m4a"
                                      onChange={(e) =>
                                        handleMediaUpload(upload.id, e)
                                      }
                                      className="hidden"
                                    />
                                    <div className="text-4xl text-gray-300 font-light mb-1">
                                      +
                                    </div>
                                    <p className="text-xs text-gray-400 font-medium text-center px-2">
                                      Audio (MP3, WAV, etc.)
                                    </p>
                                  </label>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Document/Preview Uploads */}
                    {previewUploads.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-500 mb-4">
                          {previewUploads.length}{" "}
                          {previewUploads.length === 1 ? "Document" : "Documents"}
                        </p>
                        <div className="flex flex-wrap gap-4">
                          {previewUploads.map((upload, index) => {
                            const element = canvasElements.find(
                              (el) => el.id === upload.elementId,
                            );
                            return (
                              <div key={upload.id} className="relative">
                                {/* Number Badge */}
                                <div className="absolute -top-2.5 -left-2.5 w-6 h-6 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center text-xs font-semibold z-10 shadow-sm">
                                  {index + 1}
                                </div>

                                {/* Remove Element Button */}
                                <button
                                  onClick={() =>
                                    removeCanvasElement(upload.elementId)
                                  }
                                  className="absolute -top-2.5 -right-2.5 w-6 h-6 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center hover:bg-red-50 z-10 shadow-sm"
                                  title={`Remove ${element?.label || "element"}`}
                                >
                                  <X
                                    size={14}
                                    className="text-gray-600 hover:text-red-500"
                                  />
                                </button>

                                {upload.preview ? (
                                  <div className="relative w-32 h-32 border-2 border-gray-300 rounded-lg overflow-hidden group bg-gradient-to-br from-blue-50 to-indigo-50">
                                    <div className="w-full h-full flex flex-col items-center justify-center p-2">
                                      <FileText className="text-blue-600 mb-2" size={32} />
                                      <p className="text-xs text-gray-700 font-medium text-center truncate w-full">
                                        {upload.file?.name}
                                      </p>
                                    </div>
                                    <button
                                      onClick={() =>
                                        removeMediaUpload(upload.id)
                                      }
                                      className="absolute top-1.5 right-1.5 w-5 h-5 bg-white rounded-full flex items-center justify-center hover:bg-red-50 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X size={12} className="text-red-500" />
                                    </button>
                                  </div>
                                ) : (
                                  <label className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#EA33C0] hover:bg-purple-50 transition-colors bg-white">
                                    <input
                                      type="file"
                                      accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md"
                                      onChange={(e) =>
                                        handleMediaUpload(upload.id, e)
                                      }
                                      className="hidden"
                                    />
                                    <div className="text-4xl text-gray-300 font-light mb-1">
                                      +
                                    </div>
                                    <p className="text-xs text-gray-400 font-medium text-center px-2">
                                      Document (PDF, DOC, etc.)
                                    </p>
                                  </label>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Right Sidebar - Layers Panel */}
        <div
          className={`bg-[#dedede72] border-l border-gray-300 flex flex-col overflow-hidden transition-all duration-300 ${
            showLayersPanel ? "w-56" : "w-10"
          }`}
        >
          <div className="px-2 py-3 border-b border-gray-300 flex items-center justify-between gap-2">
            {showLayersPanel && (
              <h3 className="text-sm font-semibold text-black px-2">Layers</h3>
            )}
            <button
              onClick={() => setShowLayersPanel(!showLayersPanel)}
              className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-gray-300/60 text-gray-600 hover:text-[#EA33C0] transition-colors"
              title={showLayersPanel ? "Hide layers" : "Show layers"}
            >
              {showLayersPanel ? (
                <ChevronRight size={16} />
              ) : (
                <Layers size={16} />
              )}
            </button>
          </div>
          {showLayersPanel && (
            <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
              {canvasElements
                .slice()
                .reverse()
                .map((element, reversedIndex) => {
                  // Map reversed index back to original array index
                  const originalIndex =
                    canvasElements.length - 1 - reversedIndex;
                  return (
                    <LayerItem
                      key={element.id}
                      element={element}
                      isSelected={element.id === selectedElement}
                      onSelect={() => setSelectedElement(element.id)}
                      onToggleVisibility={() =>
                        toggleLayerVisibility(element.id)
                      }
                      onRename={(name) => renameLayer(element.id, name)}
                      index={originalIndex}
                      isDragOver={dragOverLayerIndex === originalIndex}
                      onLayerDragStart={() =>
                        setDraggedLayerIndex(originalIndex)
                      }
                      onLayerDragOver={(e: React.DragEvent) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        setDragOverLayerIndex(originalIndex);
                      }}
                      onLayerDrop={() => {
                        if (
                          draggedLayerIndex !== null &&
                          draggedLayerIndex !== originalIndex
                        ) {
                          reorderLayers(draggedLayerIndex, originalIndex);
                        }
                        setDraggedLayerIndex(null);
                        setDragOverLayerIndex(null);
                      }}
                      onLayerDragEnd={() => {
                        setDraggedLayerIndex(null);
                        setDragOverLayerIndex(null);
                      }}
                    />
                  );
                })}
              {canvasElements.length === 0 && (
                <p className="text-xs text-gray-400 italic p-2">
                  No layers yet
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Canvas Element Component
interface CanvasElementProps {
  element: any;
  isSelected: boolean;
  onSelect: () => void;
  onOpenTextSettings: (x: number, y: number) => void;
  onOpenProperties: (x: number, y: number) => void;
  onOpenShapeProperties: (x: number, y: number) => void;
  onUpdatePosition: (x: number, y: number) => void;
  onUpdateSize: (width: number, height: number) => void;
  onDelete: () => void;
  canvasWidth: number;
  canvasHeight: number;
}

const CanvasElement: React.FC<CanvasElementProps> = ({
  element,
  isSelected,
  onSelect,
  onOpenTextSettings,
  onOpenProperties,
  onOpenShapeProperties,
  onUpdatePosition,
  onUpdateSize,
  onDelete,
  canvasWidth,
  canvasHeight,
}) => {
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef(false);
  const resizeDirectionRef = useRef("");
  const dragStart = useRef<{
    x: number;
    y: number;
    elemX: number;
    elemY: number;
    elemWidth: number;
    elemHeight: number;
  }>({ x: 0, y: 0, elemX: 0, elemY: 0, elemWidth: 0, elemHeight: 0 });

  // Keep stable references to the latest callbacks
  const onUpdatePositionRef = useRef(onUpdatePosition);
  const onUpdateSizeRef = useRef(onUpdateSize);
  const elementRef = useRef(element);
  onUpdatePositionRef.current = onUpdatePosition;
  onUpdateSizeRef.current = onUpdateSize;
  elementRef.current = element;

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    // Check if clicking on resize handle
    const direction = target.getAttribute("data-direction");
    if (direction) {
      e.stopPropagation();
      e.preventDefault();
      onSelect();
      isResizingRef.current = true;
      resizeDirectionRef.current = direction;

      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        elemX: element.x,
        elemY: element.y,
        elemWidth: element.width,
        elemHeight: element.height,
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return;
    }

    // Regular drag
    e.stopPropagation();
    e.preventDefault();
    onSelect();
    isDraggingRef.current = true;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      elemX: element.x,
      elemY: element.y,
      elemWidth: element.width,
      elemHeight: element.height,
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDraggingRef.current) {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;

      // Calculate new position
      let newX = dragStart.current.elemX + dx;
      let newY = dragStart.current.elemY + dy;

      // Constrain to canvas bounds
      const elWidth = elementRef.current.width;
      const elHeight = elementRef.current.height;
      newX = Math.max(0, Math.min(newX, canvasWidth - elWidth));
      newY = Math.max(0, Math.min(newY, canvasHeight - elHeight));

      onUpdatePositionRef.current(newX, newY);
    } else if (isResizingRef.current) {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      const direction = resizeDirectionRef.current;

      let newX = dragStart.current.elemX;
      let newY = dragStart.current.elemY;
      let newWidth = dragStart.current.elemWidth;
      let newHeight = dragStart.current.elemHeight;

      // Calculate new dimensions based on resize direction
      switch (direction) {
        case "se": // Southeast - bottom right
          newWidth = Math.max(50, dragStart.current.elemWidth + dx);
          newHeight = Math.max(50, dragStart.current.elemHeight + dy);
          break;
        case "nw": // Northwest - top left
          newWidth = Math.max(50, dragStart.current.elemWidth - dx);
          newHeight = Math.max(50, dragStart.current.elemHeight - dy);
          newX =
            dragStart.current.elemX + (dragStart.current.elemWidth - newWidth);
          newY =
            dragStart.current.elemY +
            (dragStart.current.elemHeight - newHeight);
          break;
        case "ne": // Northeast - top right
          newWidth = Math.max(50, dragStart.current.elemWidth + dx);
          newHeight = Math.max(50, dragStart.current.elemHeight - dy);
          newY =
            dragStart.current.elemY +
            (dragStart.current.elemHeight - newHeight);
          break;
        case "sw": // Southwest - bottom left
          newWidth = Math.max(50, dragStart.current.elemWidth - dx);
          newHeight = Math.max(50, dragStart.current.elemHeight + dy);
          newX =
            dragStart.current.elemX + (dragStart.current.elemWidth - newWidth);
          break;
        case "n": // North - top
          newHeight = Math.max(50, dragStart.current.elemHeight - dy);
          newY =
            dragStart.current.elemY +
            (dragStart.current.elemHeight - newHeight);
          break;
        case "s": // South - bottom
          newHeight = Math.max(50, dragStart.current.elemHeight + dy);
          break;
        case "e": // East - right
          newWidth = Math.max(50, dragStart.current.elemWidth + dx);
          break;
        case "w": // West - left
          newWidth = Math.max(50, dragStart.current.elemWidth - dx);
          newX =
            dragStart.current.elemX + (dragStart.current.elemWidth - newWidth);
          break;
      }

      // Constrain to canvas bounds
      if (newX < 0) {
        newWidth += newX;
        newX = 0;
      }
      if (newY < 0) {
        newHeight += newY;
        newY = 0;
      }
      if (newX + newWidth > canvasWidth) {
        newWidth = canvasWidth - newX;
      }
      if (newY + newHeight > canvasHeight) {
        newHeight = canvasHeight - newY;
      }

      onUpdatePositionRef.current(newX, newY);
      onUpdateSizeRef.current(newWidth, newHeight);
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    isResizingRef.current = false;
    resizeDirectionRef.current = "";
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Check if this is a Safe Area element
  const isSafeArea = element.templateId === "safe_area";

  return (
    <div
      className={`absolute ${isSafeArea ? "border-2 border-dashed pointer-events-none" : `border-2 ${isSelected ? "border-[#EA33C0] z-20" : "border-[#EA33C0]/40"} cursor-move`} bg-white select-none`}
      style={{
        left: element.x,
        top: element.y,
        width: element.width,
        height: element.height,
        backgroundColor: isSafeArea ? "rgba(234, 51, 192, 0.15)" : "white",
        borderColor: isSafeArea ? "#EA33C0" : undefined,
        zIndex: isSafeArea ? 1 : isSelected ? 20 : undefined,
      }}
      onMouseDown={isSafeArea ? undefined : handleMouseDown}
    >
      {/* Content */}
      {!isSafeArea && (
        <div
          className={`${element.category === "Button" ? "" : "p-3"} text-sm text-black font-medium pointer-events-none w-full h-full`}
        >
          {element.category === "Text" && element.content}
          {element.category === "Image" && (
            <ImageIcon className="text-gray-400" size={32} />
          )}
          {element.category === "Video" && (
            <div className="flex items-center justify-center w-full h-full">
              <div className="text-gray-400 text-xs">VIDEO</div>
            </div>
          )}
          {element.category === "Audio" && (
            <div className="flex items-center justify-center w-full h-full">
              <Music className="text-gray-400" size={32} />
            </div>
          )}
          {element.category === "Preview" && (
            <div className="flex items-center justify-center w-full h-full">
              <FileText className="text-gray-400" size={32} />
            </div>
          )}
          {element.category === "Shape" && (
            <div className="w-full h-full bg-purple-100 rounded" />
          )}
          {element.category === "Button" && element.buttonSettings && (
            <div
              className="flex items-center justify-center gap-2 w-full h-full font-semibold pointer-events-none"
              style={{
                background: element.buttonSettings.bg,
                color: element.buttonSettings.text,
                borderRadius: element.buttonSettings.borderRadius || "8px",
                border: element.buttonSettings.borderColor
                  ? `1px solid ${element.buttonSettings.borderColor}`
                  : "none",
                boxShadow: element.buttonSettings.shadow || "none",
                fontSize: "14px",
                letterSpacing: "0.3px",
              }}
            >
              {element.buttonSettings.icon && (
                <span className="text-base">{element.buttonSettings.icon}</span>
              )}
              <span>{element.buttonSettings.label}</span>
            </div>
          )}
        </div>
      )}

      {/* Safe Area Label */}
      {isSafeArea && (
        <div className="w-full h-full flex items-center justify-center pointer-events-none">
          <span className="text-[#EA33C0] text-xs font-semibold">SAFE AREA</span>
        </div>
      )}

      {/* Resize handles */}
      {isSelected && !isSafeArea && (
        <>
          <div
            data-direction="nw"
            className="resize-handle absolute -top-2 -left-2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-nwse-resize border-2 border-white shadow-sm"
          />
          <div
            data-direction="ne"
            className="resize-handle absolute -top-2 -right-2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-nesw-resize border-2 border-white shadow-sm"
          />
          <div
            data-direction="sw"
            className="resize-handle absolute -bottom-2 -left-2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-nesw-resize border-2 border-white shadow-sm"
          />
          <div
            data-direction="se"
            className="resize-handle absolute -bottom-2 -right-2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-nwse-resize border-2 border-white shadow-sm"
          />

          <div
            data-direction="n"
            className="resize-handle absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-ns-resize border-2 border-white shadow-sm"
          />
          <div
            data-direction="s"
            className="resize-handle absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-ns-resize border-2 border-white shadow-sm"
          />
          <div
            data-direction="w"
            className="resize-handle absolute top-1/2 -left-2 -translate-y-1/2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-ew-resize border-2 border-white shadow-sm"
          />
          <div
            data-direction="e"
            className="resize-handle absolute top-1/2 -right-2 -translate-y-1/2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-ew-resize border-2 border-white shadow-sm"
          />
        </>
      )}

      {/* Action Buttons */}
      {isSelected && !isSafeArea && (
        <div className="absolute -bottom-12 left-0 flex gap-2">
          {element.category === "Text" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOpenTextSettings(
                  Math.min(element.x, 375 - 240),
                  element.y + element.height + 50,
                );
              }}
              className="px-4 py-1.5 bg-white text-[#EA33C0] rounded-full text-xs font-semibold border-2 border-[#EA33C0] hover:bg-pink-50"
            >
              Edit Text
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (element.category === "Shape") {
                onOpenShapeProperties(385, element.y);
              } else {
                onOpenProperties(385, element.y);
              }
            }}
            className="px-4 py-1.5 bg-white text-[#EA33C0] rounded-full text-xs font-semibold border-2 border-[#EA33C0] hover:bg-pink-50"
          >
            Properties
          </button>
          {element.category === "Button" && (
            <button className="px-4 py-1.5 bg-white text-[#EA33C0] rounded-md text-xs font-semibold border-2 border-[#EA33C0] hover:bg-pink-50">
              CTA
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="px-4 py-1.5 bg-white text-red-500 rounded-full text-xs font-semibold border-2 border-red-500 hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

// Floating Text Settings Component
interface FloatingTextSettingsProps {
  element: any;
  position: { x: number; y: number };
  onClose: () => void;
  onUpdate: (key: string, value: any) => void;
  onUpdateContent: (value: string) => void;
}

const FloatingTextSettings: React.FC<FloatingTextSettingsProps> = ({
  element,
  position,
  onClose,
  onUpdate,
  onUpdateContent,
}) => {
  return (
    <div
      className="absolute bg-white rounded-lg shadow-xl border-2 border-[#EA33C0] overflow-hidden z-50"
      style={{ left: position.x, top: position.y, width: "240px" }}
    >
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#EA33C0]">
        <h3 className="font-bold text-white text-sm">Text Settings</h3>
        <button onClick={onClose} className="text-white hover:text-gray-200">
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Font
          </label>
          <div className="relative">
            <select
              value={element.textSettings?.font || ""}
              onChange={(e) => onUpdate("font", e.target.value)}
              className="w-full px-3 py-2 border-2 border-[#EA33C0]/40 rounded-md text-sm focus:outline-none focus:border-[#EA33C0] appearance-none bg-white"
            >
              <option value="">Select Option</option>
              <option value="Arial">Arial</option>
              <option value="Helvetica">Helvetica</option>
              <option value="Times New Roman">Times New Roman</option>
            </select>
            <ChevronDown
              size={14}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#EA33C0] pointer-events-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Font size (px)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="8"
              max="72"
              value={element.textSettings?.fontSize || 12}
              onChange={(e) => onUpdate("fontSize", parseInt(e.target.value))}
              className="flex-1 accent-[#EA33C0]"
            />
            <input
              type="number"
              value={element.textSettings?.fontSize || 12}
              onChange={(e) => onUpdate("fontSize", parseInt(e.target.value))}
              className="w-14 px-2 py-1.5 border-2 border-[#EA33C0]/40 rounded text-sm text-center focus:outline-none focus:border-[#EA33C0]"
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-1.5">
          <button
            onClick={() => onUpdate("bold", !element.textSettings?.bold)}
            className={`py-2 rounded font-bold text-lg transition-colors ${element.textSettings?.bold ? "bg-[#EA33C0] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
          >
            B
          </button>
          <button
            onClick={() => onUpdate("italic", !element.textSettings?.italic)}
            className={`py-2 rounded italic text-lg transition-colors ${element.textSettings?.italic ? "bg-[#EA33C0] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
          >
            I
          </button>
          <button
            onClick={() =>
              onUpdate("underline", !element.textSettings?.underline)
            }
            className={`py-2 rounded font-bold text-lg transition-colors ${element.textSettings?.underline ? "bg-[#EA33C0] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
          >
            B
          </button>
          <button
            onClick={() =>
              onUpdate("strikethrough", !element.textSettings?.strikethrough)
            }
            className={`py-2 rounded font-bold text-lg transition-colors ${element.textSettings?.strikethrough ? "bg-[#EA33C0] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
          >
            B
          </button>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Colour Picker
          </label>
          <input
            type="color"
            value={element.textSettings?.color || "#000000"}
            onChange={(e) => onUpdate("color", e.target.value)}
            className="w-full h-10 border-2 border-[#EA33C0]/40 rounded-md cursor-pointer"
          />
        </div>

        {/* Text Content Editor - At Bottom */}
        <div className="border-t border-gray-200 pt-3">
          <textarea
            value={element.content || ""}
            onChange={(e) => onUpdateContent(e.target.value)}
            className="w-full px-3 py-2 border-2 border-[#EA33C0]/40 rounded-md text-sm focus:outline-none focus:border-[#EA33C0] resize-none"
            rows={3}
            placeholder="Enter text content..."
          />
        </div>
      </div>
    </div>
  );
};

// Floating Properties Component
interface FloatingPropertiesProps {
  element: any;
  position: { x: number; y: number };
  onClose: () => void;
  onUpdate: (key: string, value: any) => void;
  onUpdateLabel: (value: string) => void;
}

const FloatingProperties: React.FC<FloatingPropertiesProps> = ({
  element,
  position,
  onClose,
  onUpdate,
  onUpdateLabel,
}) => {
  const isMediaElement =
    element.category === "Image" || element.category === "Video";

  return (
    <div
      className="absolute bg-white rounded-lg shadow-xl border-2 border-[#EA33C0] overflow-hidden z-50"
      style={{ left: position.x, top: position.y, width: "240px" }}
    >
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#EA33C0]">
        <h3 className="font-bold text-white text-sm">Properties</h3>
        <button onClick={onClose} className="text-white hover:text-gray-200">
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Content Title
          </label>
          <input
            type="text"
            value={element.label || ""}
            onChange={(e) => onUpdateLabel(e.target.value)}
            placeholder="Enter Title..."
            className="w-full px-3 py-2 border-2 border-[#EA33C0]/40 rounded-md text-sm focus:outline-none focus:border-[#EA33C0]"
          />
        </div>

        {/* Only show toggles for non-media elements */}
        {!isMediaElement && (
          <>
            <div className="flex items-center justify-between py-2">
              <label className="text-sm text-gray-800">
                Show in template only
              </label>
              <button
                onClick={() =>
                  onUpdate(
                    "showInTemplateOnly",
                    !element.properties?.showInTemplateOnly,
                  )
                }
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  element.properties?.showInTemplateOnly
                    ? "bg-[#EA33C0]"
                    : "bg-gray-300"
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                    element.properties?.showInTemplateOnly
                      ? "translate-x-6"
                      : ""
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm text-gray-800">Show in Info only</label>
              <button
                onClick={() =>
                  onUpdate(
                    "showInInfoOnly",
                    !element.properties?.showInInfoOnly,
                  )
                }
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  element.properties?.showInInfoOnly
                    ? "bg-[#EA33C0]"
                    : "bg-gray-300"
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                    element.properties?.showInInfoOnly ? "translate-x-6" : ""
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm text-gray-800">
                Show character count
              </label>
              <button
                onClick={() =>
                  onUpdate(
                    "showCharacterCount",
                    !element.properties?.showCharacterCount,
                  )
                }
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  element.properties?.showCharacterCount
                    ? "bg-[#EA33C0]"
                    : "bg-gray-300"
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                    element.properties?.showCharacterCount
                      ? "translate-x-6"
                      : ""
                  }`}
                />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Floating Shape Properties Component
interface FloatingShapePropertiesProps {
  element: any;
  position: { x: number; y: number };
  onClose: () => void;
  onUpdate: (key: string, value: any) => void;
}

const FloatingShapeProperties: React.FC<FloatingShapePropertiesProps> = ({
  element,
  position,
  onClose,
  onUpdate,
}) => {
  return (
    <div
      className="absolute bg-white rounded-lg shadow-xl border-2 border-[#EA33C0] overflow-hidden z-50"
      style={{ left: position.x, top: position.y, width: "240px" }}
    >
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#EA33C0]">
        <h3 className="font-bold text-white text-sm">Shape Properties</h3>
        <button onClick={onClose} className="text-white hover:text-gray-200">
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between py-2">
          <label className="text-sm text-gray-800">Background</label>
          <button
            onClick={() =>
              onUpdate("background", !element.shapeSettings?.background)
            }
            className={`relative w-12 h-6 rounded-full transition-colors ${
              element.shapeSettings?.background ? "bg-[#EA33C0]" : "bg-gray-300"
            }`}
          >
            <div
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                element.shapeSettings?.background ? "translate-x-6" : ""
              }`}
            />
          </button>
        </div>

        <div className="flex items-center justify-between py-2">
          <label className="text-sm text-gray-800">Overlay</label>
          <button
            onClick={() => onUpdate("overlay", !element.shapeSettings?.overlay)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              element.shapeSettings?.overlay ? "bg-[#EA33C0]" : "bg-gray-300"
            }`}
          >
            <div
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                element.shapeSettings?.overlay ? "translate-x-6" : ""
              }`}
            />
          </button>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Colour Picker
          </label>
          <input
            type="color"
            value={element.shapeSettings?.color || "#EA33C0"}
            onChange={(e) => onUpdate("color", e.target.value)}
            className="w-full h-10 border-2 border-[#EA33C0]/40 rounded-md cursor-pointer"
          />
        </div>
      </div>
    </div>
  );
};

// Layer Item Component
interface LayerItemProps {
  element: any;
  isSelected: boolean;
  onSelect: () => void;
  onToggleVisibility: () => void;
  onRename: (name: string) => void;
  index: number;
  isDragOver: boolean;
  onLayerDragStart: () => void;
  onLayerDragOver: (e: React.DragEvent) => void;
  onLayerDrop: () => void;
  onLayerDragEnd: () => void;
}

const LayerItem: React.FC<LayerItemProps> = ({
  element,
  isSelected,
  onSelect,
  onToggleVisibility,
  onRename,
  isDragOver,
  onLayerDragStart,
  onLayerDragOver,
  onLayerDrop,
  onLayerDragEnd,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(element.layerName || element.label);
  const inputRef = useRef<HTMLInputElement>(null);

  const getCategoryIcon = () => {
    switch (element.category) {
      case "Text":
        return <Type size={14} />;
      case "Image":
        return <ImageIcon size={14} />;
      case "Video":
        return <Film size={14} />;
      case "Audio":
        return <Music size={14} />;
      case "Preview":
        return <FileText size={14} />;
      case "Button":
        return <MousePointer size={14} />;
      case "Shape":
        return <Square size={14} />;
      case "Attributes":
        return <Square size={14} />;
      default:
        return <Square size={14} />;
    }
  };

  const handleStartEdit = () => {
    setEditName(element.layerName || element.label);
    setIsEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const handleConfirmRename = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== (element.layerName || element.label)) {
      onRename(trimmed);
    }
    setIsEditing(false);
  };

  const handleCancelRename = () => {
    setEditName(element.layerName || element.label);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleConfirmRename();
    } else if (e.key === "Escape") {
      handleCancelRename();
    }
  };

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        onLayerDragStart();
      }}
      onDragOver={onLayerDragOver}
      onDrop={(e) => {
        e.preventDefault();
        onLayerDrop();
      }}
      onDragEnd={onLayerDragEnd}
      className={`flex items-center gap-1.5 px-1 py-2 rounded-md cursor-pointer transition-colors group ${
        isSelected
          ? "bg-[#EA33C0]/10 border border-[#EA33C0]/30"
          : "hover:bg-gray-200/60 border border-transparent"
      } ${isDragOver ? "border-t-2 border-t-[#EA33C0]" : ""}`}
      onClick={() => {
        onSelect();
      }}
    >
      {/* Drag Handle */}
      <div
        className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-400 hover:text-[#EA33C0] transition-colors"
        onMouseDown={(e) => e.stopPropagation()}
        title="Drag to reorder"
      >
        <GripVertical size={14} />
      </div>

      {/* Category Icon */}
      <div
        className={`flex-shrink-0 ${
          isSelected ? "text-[#EA33C0]" : "text-gray-500"
        }`}
      >
        {getCategoryIcon()}
      </div>

      {/* Layer Name */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleConfirmRename}
            onKeyDown={handleKeyDown}
            className="w-full text-xs bg-white border border-[#EA33C0]/40 rounded px-1.5 py-0.5 outline-none focus:border-[#EA33C0]"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className={`text-xs truncate block ${
              isSelected ? "text-[#EA33C0] font-semibold" : "text-black"
            } ${element.visible === false ? "opacity-40" : ""}`}
            onDoubleClick={(e) => {
              e.stopPropagation();
              handleStartEdit();
            }}
            title={element.layerName || element.label}
          >
            {element.layerName || element.label}
          </span>
        )}
      </div>

      {/* Edit Button */}
      {!isEditing && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleStartEdit();
          }}
          className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-[#EA33C0]"
          title="Rename layer"
        >
          <Pencil size={12} />
        </button>
      )}

      {/* Visibility Toggle */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleVisibility();
        }}
        className={`flex-shrink-0 transition-colors ${
          element.visible === false
            ? "text-gray-300 hover:text-gray-500"
            : "text-gray-500 hover:text-[#EA33C0]"
        }`}
        title={element.visible === false ? "Show layer" : "Hide layer"}
      >
        {element.visible === false ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
};

export default AdBuilder;
